/* global CustomDatafeed */
import React, { useEffect, useRef, useState } from "react";
import { REACT_APP_API_BASE_URL } from "../../apiurl";
import "./TradingViewChart.css";

console.log("[TV DRAWING PERSISTENCE BUILD]", "v4-serialization-fix");

/**
 * Cross-iframe Map detection.
 * 
 * TradingView runs inside an iframe. Maps created in the iframe's JS context
 * fail `instanceof Map` when checked from the parent window because each
 * window has its own Map constructor. Duck-typing works across contexts.
 */
function isMapLike(obj) {
    return obj != null && typeof obj.entries === 'function' && typeof obj.size === 'number';
}

/**
 * Serialize TradingView LineToolsAndGroupsState for JSON transport.
 * 
 * v30.2.0 state shape:
 *   sources: Map<EntityId, LineToolState | null> | null
 *   groups: Map<string, LineToolsGroupState | null>
 *   symbol?: string
 * 
 * Uses isMapLike() instead of instanceof Map to handle cross-iframe Maps.
 * Converts Maps to [key, value] entry arrays for JSON serialization.
 */
function serializeDrawingState(state) {
    if (!state) return null;

    let sources;
    if (isMapLike(state.sources)) {
        sources = Array.from(state.sources.entries());
    } else if (Array.isArray(state.sources)) {
        sources = state.sources;
    } else {
        sources = [];
    }

    let groups;
    if (isMapLike(state.groups)) {
        groups = Array.from(state.groups.entries());
    } else if (Array.isArray(state.groups)) {
        groups = state.groups;
    } else {
        groups = [];
    }

    return {
        sources,
        groups,
        symbol: state.symbol
    };
}

/**
 * Deserialize stored drawing state back to Maps for TradingView v30.2.
 * 
 * Converts [key, value] entry arrays back to Maps.
 * Returns Partial<LineToolsAndGroupsState> | null as expected by loadLineToolsAndGroups.
 */
function deserializeDrawingState(data) {
    if (!data) return null;

    const result = {};

    if (Array.isArray(data.sources)) {
        result.sources = new Map(data.sources);
    } else {
        result.sources = new Map();
    }

    if (Array.isArray(data.groups)) {
        result.groups = new Map(data.groups);
    } else {
        result.groups = new Map();
    }

    if (data.symbol !== undefined) {
        result.symbol = data.symbol;
    }

    return result;
}

/**
 * Normalize a TradingView symbol to a canonical storage key.
 * 
 * Strips exchange prefix (NSE:, BSE:) and instrument suffix (-EQ, -BE, etc).
 * Consistent across save, load, symbol switch, and timeframe switch.
 * 
 * Examples:
 *   "NSE:RELIANCE-EQ" → "RELIANCE"
 *   "BSE:TCS"         → "TCS"
 *   "RELIANCE-EQ"     → "RELIANCE"
 *   "RELIANCE"         → "RELIANCE"
 */
function normalizeSymbol(rawSymbol) {
    if (!rawSymbol) return null;
    let sym = rawSymbol.replace(/^(NSE:|BSE:)/, "");
    if (sym.includes("-")) sym = sym.split("-")[0];
    return sym || null;
}

function generateUUID() {
   return 'draw-xxxx-4xxx-yxxx-xxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
   });
}

const SESSION_ID = 'adm-' + generateUUID();

const TradingViewChart = ({ symbol, exchange = "NSE", interval = "1D" }) => {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = false;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const loadTradingView = async () => {
      try {
        const baseUrl = REACT_APP_API_BASE_URL.endsWith("/") 
            ? REACT_APP_API_BASE_URL 
            : `${REACT_APP_API_BASE_URL}/`;

        await loadScript(`${baseUrl}api/charting_library-master/charting_library/charting_library.standalone.js`);
        await loadScript(`${baseUrl}api/charting_library-master/custom_datafeed.js`);

        setIsLoaded(true);
      } catch (err) {
        console.error("Failed to load TradingView scripts", err);
      }
    };

    loadTradingView();
  }, []);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || !window.TradingView) return;
    
    // If symbol changes, we must flush/remove the old widget completely
    if (widgetRef.current) {
        try {
            widgetRef.current.remove();
            widgetRef.current = null;
        } catch (e) {
            console.warn("Error removing previous widget", e);
        }
    }

    const initWidget = () => {
        if (!window.CustomDatafeed && typeof CustomDatafeed === "undefined") {
             setTimeout(initWidget, 100);
             return;
        }

        const datafeed = typeof CustomDatafeed !== "undefined" ? CustomDatafeed : window.CustomDatafeed;
        const baseUrl = REACT_APP_API_BASE_URL.endsWith("/") ? REACT_APP_API_BASE_URL.slice(0, -1) : REACT_APP_API_BASE_URL;
        window.backendUrl = baseUrl;

        // Canonical symbol for this chart, used as the drawing storage key
        const canonicalSymbol = normalizeSymbol(symbol) || 'UNKNOWN';
        // Exchange comes from parent (selectedStock.exch_seg) — never inferred from symbol alone
        const canonicalExchange = exchange || 'NSE';

        // ── Build the save_load_adapter ────────────────────────────────
        const saveLoadAdapter = {
            // ─── Chart layout methods ────────────────────────────────

            /**
             * getAllCharts(): Promise<ChartMetaInfo[]>
             * 
             * v30.2 ChartMetaInfo = { id, name, symbol, resolution, timestamp }
             */
            getAllCharts: () => {
                const params = new URLSearchParams({
                    symbol: canonicalSymbol,
                    exchange: canonicalExchange
                });
                return fetch(`${baseUrl}/api/chart/list?${params.toString()}`, {
                    credentials: 'include'
                })
                    .then(res => res.json())
                    .then(data => {
                        if (data.status !== 'ok' || !Array.isArray(data.data)) {
                            console.log("[TV GET ALL CHARTS] No charts or error:", data);
                            return [];
                        }
                        const charts = data.data.map(item => ({
                            id: String(item.id),
                            name: item.name || `Chart ${item.id}`,
                            symbol: item.symbol || '',
                            resolution: item.resolution || '1D',
                            timestamp: item.timestamp || Math.floor(Date.now() / 1000)
                        }));
                        console.log("[TV GET ALL CHARTS]", charts.length, "layouts found");
                        return charts;
                    })
                    .catch(err => {
                        console.error("[TV GET ALL CHARTS] ERROR:", err);
                        return [];
                    });
            },

            removeChart: (chartId) => {
                return fetch(`${baseUrl}/api/chart/remove/${chartId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                }).then(res => res.json());
            },

            /**
             * saveChart(chartData: ChartData): Promise<string | number>
             * 
             * v30.2 ChartData = { id?, name, symbol, resolution, content: string, timestamp }
             * Returns the chart's unique ID (becomes layoutId for drawings).
             * 
             * content is a STRING from TradingView — we parse it only to embed
             * name/resolution (no dedicated DB columns), then store as JSONB.
             */
            saveChart: (chartData) => {
                console.log("[TV CHART SAVE]", {
                    id: chartData.id,
                    name: chartData.name,
                    symbol: chartData.symbol,
                    resolution: chartData.resolution,
                    contentLength: chartData.content?.length
                });

                // Use the authoritative exchange from the component prop, not from symbol prefix
                const chartExchange = canonicalExchange;

                const sym = normalizeSymbol(chartData.symbol) || canonicalSymbol;

                // Parse content string to embed metadata for round-trip
                let contentObj;
                try {
                    contentObj = typeof chartData.content === 'string' 
                        ? JSON.parse(chartData.content) 
                        : (chartData.content || {});
                } catch (e) {
                    contentObj = {};
                }

                const payload = { 
                    id: chartData.id,       // undefined on first save, DB id on update
                    name: chartData.name, 
                    symbol: sym, 
                    exchange: chartExchange,
                    resolution: chartData.resolution, 
                    content: contentObj      // Backend embeds name/resolution and stores as JSONB
                };

                return fetch(`${baseUrl}/api/chart/save`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(payload)
                })
                    .then(res => res.json())
                    .then(data => { 
                        if (data.status === 'ok') {
                            console.log("[TV CHART SAVE] SUCCESS id:", data.id);
                            return data.id; 
                        }
                        console.error("[TV CHART SAVE] FAILED:", data.message);
                        throw new Error(data.message || 'Save failed'); 
                    })
                    .catch(err => { 
                        console.error("[TV CHART SAVE] ERROR:", err);
                        throw err; 
                    });
            },

            /**
             * getChartContent(chartId): Promise<string>
             * 
             * TradingView expects the chart content STRING that was
             * originally in ChartData.content when saveChart was called.
             */
            getChartContent: (chartId) => {
                return fetch(`${baseUrl}/api/chart/list`, {
                    credentials: 'include'
                })
                    .then(res => res.json())
                    .then(data => {
                        if (data.status !== 'ok' || !Array.isArray(data.data)) return null;
                        
                        const chart = data.data.find(c => String(c.id) === String(chartId));
                        if (!chart || !chart.content) {
                            console.warn("[TV GET CHART CONTENT] Not found:", chartId);
                            return null;
                        }

                        // TradingView expects a string
                        const contentStr = (typeof chart.content === 'object') 
                            ? JSON.stringify(chart.content) 
                            : chart.content;
                        
                        console.log("[TV GET CHART CONTENT] id:", chartId, "length:", contentStr?.length);
                        return contentStr;
                    })
                    .catch(err => {
                        console.error("[TV GET CHART CONTENT] ERROR:", err);
                        return null;
                    });
            },

            // ─── Study template stubs ────────────────────────────────
            getAllStudyTemplates: () => Promise.resolve([]),
            removeStudyTemplate: () => Promise.resolve(),
            saveStudyTemplate: () => Promise.resolve(),
            getStudyTemplateContent: () => Promise.resolve(null),

            // ─── Drawing template stubs ──────────────────────────────
            getDrawingTemplates: () => Promise.resolve([]),
            loadDrawingTemplate: () => Promise.resolve(''),
            removeDrawingTemplate: () => Promise.resolve(),
            saveDrawingTemplate: () => Promise.resolve(),

            // ─── Chart template stubs ────────────────────────────────
            getChartTemplateContent: () => Promise.resolve({}),
            getAllChartTemplates: () => Promise.resolve([]),
            saveChartTemplate: () => Promise.resolve(),
            removeChartTemplate: () => Promise.resolve(),

            // ═══════════════════════════════════════════════════════════
            // DRAWING PERSISTENCE — TradingView v30.2 separate storage
            // 
            // v30.2 IExternalSaveLoadAdapter:
            //   saveLineToolsAndGroups(layoutId, chartId, state): Promise<void>
            //   loadLineToolsAndGroups(layoutId, chartId, requestType, requestContext): Promise<Partial<LineToolsAndGroupsState> | null>
            // ═══════════════════════════════════════════════════════════

            saveLineToolsAndGroups: (layoutId, chartId, state) => {
                const reqId = generateUUID();
                const wrapperSymbol = canonicalSymbol;
                const wrapperExchange = canonicalExchange;
                let activeSymbol = wrapperSymbol;
                let activeExchange = wrapperExchange;

                try {
                    if (window.tvWidget && window.tvWidget.activeChart) {
                        const chartSym = window.tvWidget.activeChart().symbol();
                        if (chartSym) {
                            activeSymbol = chartSym;
                            if (activeSymbol.includes(':')) {
                                const parts = activeSymbol.split(':');
                                activeExchange = parts[0];
                                activeSymbol = parts[1];
                            }
                        }
                    }
                } catch (e) {
                    console.warn("Failed to read activeChart symbol", e);
                }

                const resolvedSymbol = normalizeSymbol(activeSymbol) || '_global_';
                const resolvedExchange = activeExchange;

                if (normalizeSymbol(wrapperSymbol) !== resolvedSymbol || wrapperExchange !== resolvedExchange) {
                    console.warn(`[DrawingContextMismatch] client=admin sessionId=${SESSION_ID} reqId=${reqId} wrapperSymbol=${wrapperSymbol} wrapperExchange=${wrapperExchange} resolvedSymbol=${resolvedSymbol} resolvedExchange=${resolvedExchange}`);
                }

                const serialized = serializeDrawingState(state);
                const sourcesCount = state?.sources ? (isMapLike(state.sources) ? state.sources.size : state.sources.length) : 0;
                const groupsCount = state?.groups ? (isMapLike(state.groups) ? state.groups.size : state.groups.length) : 0;

                console.log(`[TV_DRAWING_CALLBACK] sessionId=${SESSION_ID} reqId=${reqId} activeSymbol=${activeSymbol} activeExchange=${activeExchange} wrapperSymbol=${wrapperSymbol} wrapperExchange=${wrapperExchange} sourcesCount=${sourcesCount} groupsCount=${groupsCount}`);

                return fetch(`${baseUrl}/api/chart/drawings/save`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        reqId: reqId,
                        sessionId: SESSION_ID,
                        clientSource: 'admin',
                        symbol: resolvedSymbol,
                        exchange: resolvedExchange,
                        layoutId: layoutId,
                        chartId: chartId,
                        state: serialized
                    })
                })
                .then(res => {
                    if (!res.ok) {
                        console.error(`[DrawingSave] FAILED status=${res.status}`);
                        throw new Error(`Save failed: ${res.status}`);
                    }
                    return res.json();
                })
                .then(data => {
                    console.log(`[DrawingSave] SUCCESS symbol=${resolvedSymbol} sources=${sourcesCount}`);
                })
                .catch(err => {
                    console.error('[DrawingSave] ERROR:', err);
                });
            },

            loadLineToolsAndGroups: (layoutId, chartId, requestType, requestContext) => {
                console.log("[TV DRAWING LOAD REQUEST]", {
                    layoutId,
                    chartId,
                    requestType,
                    requestContext
                });

                // Only load for mainSeriesLineTools (normal drawings on the main chart)
                if (requestType !== 'mainSeriesLineTools') {
                    console.log(`[DrawingLoad] Skipping non-main request type: ${requestType}`);
                    return Promise.resolve(null);
                }

                // Symbol resolution priority (same as save):
                // 1. requestContext.symbol (TradingView passes this for mainSeriesLineTools)
                // 2. React component's symbol prop
                // 3. '_global_' only as last resort
                const rawSymbol = requestContext?.symbol || symbol;
                const querySymbol = normalizeSymbol(rawSymbol) || '_global_';

                return fetch(`${baseUrl}/api/chart/drawings/load?symbol=${encodeURIComponent(querySymbol)}&exchange=${encodeURIComponent(canonicalExchange)}`, {
                    credentials: 'include'
                })
                .then(res => {
                    if (!res.ok) {
                        console.error(`[DrawingLoad] FAILED status=${res.status}`);
                        return null;
                    }
                    return res.json();
                })
                .then(data => {
                    if (!data || data.status !== 'ok' || !data.data) {
                        console.log(`[DrawingLoad] No drawings for symbol=${querySymbol}`);
                        return null;
                    }

                    // Phase 3: Reconstruct Maps from stored [key, value] entries
                    const deserialized = deserializeDrawingState(data.data.state);

                    console.log("[DRAWING LOAD RESULT]", {
                        symbol: querySymbol,
                        sourcesIsMap: deserialized?.sources instanceof Map,
                        groupsIsMap: deserialized?.groups instanceof Map,
                        sourcesSize: deserialized?.sources?.size,
                        groupsSize: deserialized?.groups?.size
                    });

                    return deserialized;
                })
                .catch(err => {
                    console.error('[DrawingLoad] ERROR:', err);
                    return null;
                });
            }
        };

        // ── Build widget options ───────────────────────────────────────
        const widgetOptions = {
            container: containerRef.current,
            symbol: symbol,
            interval: interval,
            fullscreen: false,
            autosize: true,
            timezone: "Asia/Kolkata",
            locale: "en",
            theme: "light",
            datafeed: datafeed,
            library_path: `${baseUrl}/api/charting_library-master/charting_library/`,
            auto_save_delay: 5,
            load_last_chart: true,

            disabled_features: [
                "use_localstorage_for_settings_save", 
                "header_compare", 
                "header_undo_redo",
                "hide_left_toolbar_by_default", 
            ],
            enabled_features: [
                "header_saveload", 
                "header_widget", 
                "header_indicators", 
                "drawing_templates",
                "header_fullscreen_button",
                "side_toolbar_in_fullscreen_mode",
                "saveload_separate_drawings_storage"
            ],

            save_load_adapter: saveLoadAdapter,

            debug: false,
            market_status_button: true,
            marketIsOpen: true
        };

        console.log("[TV CONFIG]", {
            separateDrawingsEnabled: true,
            canonicalSymbol: canonicalSymbol,
            adapterMethods: {
                saveChart: typeof saveLoadAdapter.saveChart,
                getChartContent: typeof saveLoadAdapter.getChartContent,
                getAllCharts: typeof saveLoadAdapter.getAllCharts,
                saveLineToolsAndGroups: typeof saveLoadAdapter.saveLineToolsAndGroups,
                loadLineToolsAndGroups: typeof saveLoadAdapter.loadLineToolsAndGroups
            }
        });

        const widget = new window.TradingView.widget(widgetOptions);

        // Expose debug references
        window.tvWidget = widget;
        window.tvSaveLoadAdapter = saveLoadAdapter;

        widget.onChartReady(() => {
            console.log("[TV READY] symbol:", symbol, "canonicalSymbol:", canonicalSymbol);
            
            // Native auto-save subscription
            widget.subscribe('onAutoSaveNeeded', () => {
                console.log('[TV AUTO SAVE NEEDED]');
                widget.saveChartToServer(
                    () => console.log('[TV AUTO SAVE SUCCESS]'),
                    (err) => console.error('[TV AUTO SAVE FAILED]', err),
                    { defaultChartName: `${canonicalExchange}:${canonicalSymbol} Chart` }
                );
            });
        });

        widgetRef.current = widget;
    };

    initWidget();

    return () => {
        if (widgetRef.current) {
            try {
                widgetRef.current.remove();
                widgetRef.current = null;
            } catch (e) {
                console.warn("Error removing widget", e);
            }
        }
    };
  }, [isLoaded, symbol, exchange, interval]);

  return (
    <div className="tradingview-chart-container">
      <div id="tv_chart_container" ref={containerRef} className="tradingview-chart-wrapper" />
    </div>
  );
};

export default React.memo(TradingViewChart);
